/** Ayudantes de presentación. Aquí no hay reglas de negocio, nomás modo de leerse. */

const LOCALE = "es-MX";
const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" });

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(LOCALE, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(LOCALE, { month: "short", day: "numeric" });
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 86_400_000],
  ["month", 30 * 86_400_000],
  ["week", 7 * 86_400_000],
  ["day", 86_400_000],
  ["hour", 3_600_000],
  ["minute", 60_000],
];

export function formatRelative(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const diff = date.getTime() - Date.now();
  for (const [unit, ms] of UNITS) {
    if (Math.abs(diff) >= ms) return rtf.format(Math.round(diff / ms), unit);
  }
  return rtf.format(Math.round(diff / 1000), "second");
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms}ms`;
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  // Los gigas hacían falta desde que esto también mide el disco del servidor:
  // «10240.0 MB» es un número que hay que traducir en la cabeza para entenderlo.
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Los estados viajan en inglés y en MAYÚSCULAS por la API (son el contrato con
 * la base de datos). Aquí es donde se visten de español para la banda.
 */
const ETIQUETAS_ESTADO: Record<string, string> = {
  RUNNING: "Corriendo",
  SUCCESS: "Salió bien",
  FAILED: "Falló",
  SKIPPED: "Saltada",
  SCHEDULED: "A su hora",
  MANUAL: "A mano",
  RECOVERY: "Rescatada",
};

export function statusLabel(status: string): string {
  return (
    ETIQUETAS_ESTADO[status] ??
    status.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
  );
}

export type StatusTone = "ok" | "warn" | "bad" | "info" | "neutral";

export function statusTone(status: string): StatusTone {
  switch (status) {
    case "SUCCESS":
      return "ok";
    case "FAILED":
      return "bad";
    case "RUNNING":
      return "info";
    default:
      return "neutral";
  }
}

export function truncate(text: string, max = 140): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** «12.4 k tokens», «850 tokens», «1.2 M tokens». */
export function formatTokens(valor: number): string {
  return `${milesCortos(valor)} tokens`;
}

/** El costo cuando el proveedor lo reportó; si no, vacío. */
export function formatUsd(valor: number | null | undefined): string {
  if (!valor) return "";
  return valor < 0.01 ? "< $0.01" : `$${valor.toFixed(2)}`;
}

export function milesCortos(valor: number): string {
  if (valor < 1_000) return String(valor);
  if (valor < 10_000) return `${(valor / 1_000).toFixed(1)} k`;
  if (valor < 1_000_000) return `${(valor / 1_000).toFixed(0)} k`;
  return `${(valor / 1_000_000).toFixed(1)} M`;
}
